<template>
  <div class="w-full">
    <InputNumber
      v-model="years"
      class="w-full"
      suffix=" лет"
      :invalid="invalid"
      :readonly="readonly"
      :disabled="disabled"
      :placeholder="placeholder"
      :inputId="inputId"
      :min="minYears"
      :max="maxYears"
      :use-grouping="false"
      :pt="{
        input: {
          root: ' rounded-xl max-h-8 border border-[#BDBDBD] px-4 py-2'
        }
      }"
      @blur="emits('blur', $event)"
      @focus="emits('focus', $event)"
    />

    <!-- Слайдер -->
    <div
      v-if="showSlider"
      class="mt-3"
    >
      <Slider
        v-model="uiYears"
        :min="minYears"
        :max="maxYears"
        :step="1"
        :disabled="disabled"
        class="w-full"
        :class="{ 'opacity-50': disabled }"
        :pt="{
          handle: 'bg-[#262626] rounded-full w-3 h-3 top-1.5 border border-[#262626]',
          range: 'bg-[#262626]'
        }"
      />
      <div class="flex justify-between text-xs text-gray-500 mt-1">
        <span>{{ minYears }} {{ pluralizeYears(minYears) }}</span>
        <span>{{ maxYears }} {{ pluralizeYears(maxYears) }}</span>
      </div>
    </div>

    <!-- Кнопки пресетов -->
    <SelectButton
      v-if="showSlider"
      v-model="years"
      :options="yearOptions"
      optionLabel="label"
      optionValue="value"
      :allowEmpty="false"
      :disabled="disabled"
      class="flex flex-wrap gap-1 mt-2 text-xs"
      :pt="{
        button: ({ context }) => [
          'text-[13px] p-2 rounded-xl border transition border-solid',
          context.active
            ? 'bg-[#3F3F3F] text-white border-[#BDBDBD]'
            : 'bg-white text-[#3F3F3F] border-gray-300 hover:bg-gray-50'
        ]
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { defineModel, ref, watch, computed } from 'vue';
import InputNumber, {
  type InputNumberBlurEvent,
  type InputNumberPassThroughOptions
} from 'primevue/inputnumber';
import Slider from 'primevue/slider';
import SelectButton from 'primevue/selectbutton';
import { convertMonthsToYears, convertYearsToMonths } from '@/shared';

const props = withDefaults(
  defineProps<{
    invalid?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    placeholder?: string;
    inputId?: string;
    pt?: InputNumberPassThroughOptions;

    /** минимальное значение в МЕСЯЦАХ */
    min?: number;
    /** максимальное значение в МЕСЯЦАХ (по умолчанию 30 лет) */
    max?: number;

    showSlider?: boolean;
    showPresets?: boolean;

    defaultYears?: number;
  }>(),
  {
    min: 12,
    max: 30 * 12,
    showSlider: true,
    showPresets: true
  }
);

const emits = defineEmits<{
  blur: [InputNumberBlurEvent];
  focus: [Event];
}>();

/** модель в месяцах */
const month = defineModel<number | null>();

/** переводы и округления */
const toYearsInt = (m: number) => Math.round(convertMonthsToYears(m));

/** границы в ГОДАХ (из месяцев) */
const minYears = computed(() => {
  const m = Math.max(0, props.min ?? 12);

  return Math.max(1, Math.ceil(convertMonthsToYears(m)));
});
const maxYears = computed(() => {
  const m = Math.max(props.max ?? 30 * 12, props.min ?? 12);
  return Math.max(minYears.value, Math.floor(convertMonthsToYears(m)));
});

/** локальная модель в годах (всегда число) */
const years = ref<number>(minYears.value);

const normalizedDefaultYears = computed<number | undefined>(() => {
  if (props.defaultYears == null) return undefined;
  return Math.min(Math.max(props.defaultYears, minYears.value), maxYears.value);
});

/** зажим в диапазон */
const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/** двусторонняя обёртка для слайдера (в диапазоне) */
const uiYears = computed<number>({
  get: () => clamp(years.value, minYears.value, maxYears.value),
  set: v => {
    years.value = clamp(v, minYears.value, maxYears.value);
  }
});

/** склонение “год/года/лет” */
const pluralizeYears = (n: number) => {
  const mod10 = n % 10,
    mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'год';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'года';
  return 'лет';
};

/** опции для SelectButton: шаг 5 лет */
type YearOption = { label: string; value: number };
const yearOptions = computed<YearOption[]>(() => {
  const allowed = [10, 15, 20, 30];
  return allowed
    .filter(v => v >= minYears.value && v <= maxYears.value)
    .map(v => ({ label: `${v} ${pluralizeYears(v)}`, value: v }));
});

/** избежание циклов синхронизации */
const syncing = ref(false);

/** month -> years (месяцы приходят извне; приводим к годам и зажимаем) */
watch(
  month,
  m => {
    if (syncing.value) return;
    syncing.value = true;
    const y = m != null ? toYearsInt(m) : normalizedDefaultYears.value ?? minYears.value;
    years.value = clamp(y, minYears.value, maxYears.value);
    syncing.value = false;
  },
  { immediate: true }
);

/** years -> month (наружу отдаём месяцы как кратные 12) */
watch(years, y => {
  const yClamped = clamp(y ?? minYears.value, minYears.value, maxYears.value);
  if (yClamped !== y) years.value = yClamped;
  if (syncing.value) return;
  syncing.value = true;
  month.value = convertYearsToMonths(yClamped);
  syncing.value = false;
});

/** при изменении границ поджимаем значение и синхронизируем месяц */
watch([minYears, maxYears], () => {
  if (month.value == null && normalizedDefaultYears.value != null) {
    years.value = clamp(normalizedDefaultYears.value, minYears.value, maxYears.value);
  } else {
    years.value = clamp(years.value, minYears.value, maxYears.value);
  }
  month.value = convertYearsToMonths(years.value);
});

watch(normalizedDefaultYears, (ny, old) => {
  if (ny == null || ny === old) return;
  if (month.value != null) return;

  syncing.value = true;
  years.value = clamp(ny, minYears.value, maxYears.value);
  month.value = convertYearsToMonths(years.value);
  syncing.value = false;
});
</script>
